import { ChangeEvent, FC, Fragment, memo, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { ApolloError, QueryResult, useMutation, useQuery } from '@apollo/client';
import {
  Button,
  Checkbox,
  Drawer,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@material-ui/core';
import {
  Exact,
  GetProjectsDocument,
  GetProjectsQuery,
  GetProjectsQueryVariables,
  InputMaybe,
  CreateProjectDocument,
  CreateProjectMutation,
  CreateProjectMutationVariables,
  Project,
} from '../../../../generated/graphql';
import { Container as DrawerContainer, Footer as DrawerFooter } from '../Drawer';
import {
  COLOR_BORDER,
  COLOR_BUTTON_BACKGROUND,
  COLOR_BUTTON_TEXT,
  COLOR_DISABLE_BACKGROUND,
  COLOR_EFFECT,
  COLOR_SHADOW,
  COLOR_TEXT_DISABLE,
  COLOR_TEXT_LINK,
  COLOR_TEXT_MAIN,
  COLOR_WARNING,
} from '../../../../theme/colors';
import LoadingButton from '../../primitive/LoadingButton';
import globalState from '../../../../operations/commands';
import mutationVariables from '../../../../utility/mutationVariables';

const _DrawerCreateExperience: FC<{
  getProjects: QueryResult<
    GetProjectsQuery,
    Exact<{
      organization_id: string;
      search_string?: InputMaybe<string> | undefined;
    }>
  >;
  organization_id: string;
  isOpenDrawerCreateExperience: boolean;
  setIsOpenDrawerCreateExperience: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({ getProjects, organization_id, isOpenDrawerCreateExperience, setIsOpenDrawerCreateExperience }) => {
  const [title, setTitle] = useState<string>('新規エクスペリエンス');
  const [targetURL, setTargetURL] = useState('http://');
  const [device, setDevice] = useState('all');
  const [isMessageNotDuplicated, setIsMessageNotDuplicated] = useState<boolean>(false);
  const [isMessageDuplicated, setIsMessageDuplicated] = useState<Project[] | undefined | null>();
  const [searchString, setSearchString] = useState<string>('');

  useQuery<GetProjectsQuery, GetProjectsQueryVariables>(GetProjectsDocument, {
    skip: !searchString,
    variables: {
      organization_id: organization_id ?? '',
      search_string: searchString ? `${searchString}` : '',
    },
    context: { clientName: 'planbcd' },
    onCompleted: (data: GetProjectsQuery): void => {
      if (data?.projects?.length) {
        setIsMessageNotDuplicated(false);
        setIsMessageDuplicated(data?.projects);
      } else {
        setIsMessageNotDuplicated(true);
        setIsMessageDuplicated(null);
      }
    },
    onError: (error: ApolloError) => {
      console.error(error);
      globalState.setErrors({
        message: 'error',
        name: 'NonFatalError',
        context: { clientName: 'search_console' },
      });
    },
  });

  const [createProject] = useMutation<CreateProjectMutation, CreateProjectMutationVariables>(CreateProjectDocument, {
    context: { clientName: 'planbcd' },
    onCompleted: () => {
      getProjects.refetch({ variables: { organization_id: organization_id } } as Partial<
        Exact<{ organization_id: string; search_string?: InputMaybe<string> | undefined }>
      >);
    },
    onError: (error: ApolloError) => {
      console.error(error);
    },
  });

  /**
   * フォーム初期化
   */
  const clearFormInput = () => {
    setTitle('新規エクスペリエンス');
    setTargetURL('http://');
    setDevice('all');
    setIsMessageNotDuplicated(false);
    setIsMessageDuplicated(undefined);
    setSearchString('');
  };

  /**
   * エクスペリエンス作成
   */
  const createExperiment = () => {
    // {
    //   "title": "新規エクスペリエンス",
    //   "url": "https://intelligence-local.pbcd.net:8000/engine/renewal_experiments?order=created_at_desc&org=13294",
    //   "$target_devices": "all",
    //   "$focus": false,
    //   "is_mobile_web": false
    // }
    const _project = {
      title: title,
      url: targetURL,
      $target_devices: device,
      $focus: false,
      is_mobile_web: false,
    };

    createProject({
      variables: {
        input: {
          organization_id: organization_id,
          project: _project,
        },
      },
    });
    setIsOpenDrawerCreateExperience(false);
    clearFormInput();
  };

  /**
   * 「エクスペリエンスを作成する」ボタンのdisabled判定
   */
  const isDisableSubmitButtonMemo = useMemo(() => {
    if (!/^https?:\/\/[\w/:%#$&?()~.=+-]+$/.test(targetURL)) {
      return true;
    }
    if (!title) {
      return true;
    }
    return false;
  }, [targetURL, title]);

  return (
    <Fragment>
      {/* 新規エクスペリエンスを作成 */}
      <Drawer anchor="right" open={isOpenDrawerCreateExperience}>
        <DrawerContainer>
          <DrawerContent>
            <Content>
              <PaperContent>
                <Header>
                  <Title>新規エクスペリエンスを作成</Title>
                </Header>
                <TitleSubComment>
                  【お知らせ】配信比率の最適化がデフォルトではOFFになりました。詳しくは
                  <Link href="https://support.kaizenplatform.net/hc/ja/articles/900004661026">こちら</Link>
                </TitleSubComment>
              </PaperContent>

              <PaperContent>
                <div>
                  タイトル<SmallRed>必須</SmallRed>
                </div>
                <Input
                  type="text"
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setTitle(e.target.value.slice(0, 50))}
                  value={title}
                />
                <NumberOfCharactar>{title?.length}/50</NumberOfCharactar>
              </PaperContent>

              <Space />

              <PaperContent>
                <div>
                  デザインを改善するページのURL<SmallRed>必須</SmallRed>
                </div>
                <FlexInputArea>
                  <FlexInput
                    type="text"
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setTargetURL(e.target.value)}
                    value={targetURL}
                  />
                  <FlexSecondaryButton
                    disabled={/^https?:\/\/[\w/:%#$&?()~.=+-]+$/.test(targetURL) ? false : true}
                    onClick={() => {
                      setSearchString(
                        `page=1&state%5B%5D=running&state%5B%5D=not_active&url=${encodeURIComponent(targetURL)}`,
                      );
                    }}
                  >
                    URL重複チェック
                  </FlexSecondaryButton>
                </FlexInputArea>

                {/* URL重複チェック結果メッセージ */}
                <Fragment>
                  {isMessageNotDuplicated && <NormalMessage>このURLはまだ利用されていません。</NormalMessage>}

                  {isMessageDuplicated?.length && (
                    <div>
                      <AlertMessage>このURLはすでに以下エクスペリエンスで利用されています。</AlertMessage>
                      {isMessageDuplicated.map((experiment: Project) => (
                        <Link href={'#'} key={`experiment_title_${experiment?.id}`}>
                          <div>{experiment?.title}</div>
                        </Link>
                      ))}
                    </div>
                  )}
                </Fragment>
              </PaperContent>

              <PaperContent>
                <FormControl>
                  <div>
                    対象デバイス<SmallRed>必須</SmallRed>
                  </div>
                  <RadioGroup
                    aria-label="device"
                    name="device"
                    value={device}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      setDevice(e.target.value);
                    }}
                  >
                    <FormControlLabelMui
                      value="all"
                      control={<Radio />}
                      label="すべて（スマートフォン兼用のレスポンシブデザインなど）"
                    />
                    <FormControlLabelMui value="pc" control={<Radio />} label="PCのみ" />
                    <FormControlLabelMui value="sp" control={<Radio />} label="スマートフォンのみ" />
                  </RadioGroup>
                </FormControl>
              </PaperContent>
            </Content>
          </DrawerContent>
          <DrawerFooter>
            <SecondaryButton
              onClick={() => {
                setIsOpenDrawerCreateExperience(false);
                clearFormInput();
              }}
            >
              キャンセル
            </SecondaryButton>
            <PrimaryButton loading={false} disabled={isDisableSubmitButtonMemo} onClick={createExperiment}>
              エクスペリエンスを作成する
            </PrimaryButton>
          </DrawerFooter>
        </DrawerContainer>
      </Drawer>
    </Fragment>
  );
};

/**
 * (仮)
 * 引き継ぐ設定を選んで新規エクスペリエンスを作成
 */
export const _DrawerCreateExperienceSimilar: FC<{
  getProjects: QueryResult<
    GetProjectsQuery,
    Exact<{
      organization_id: string;
      search_string?: InputMaybe<string> | undefined;
    }>
  >;
  organization_id: string;
  isOpenDrawerCreateExperienceSimilar: boolean;
  setIsOpenDrawerCreateExperienceSimilar: (arg0: boolean) => void;
}> = ({
  getProjects,
  organization_id,
  isOpenDrawerCreateExperienceSimilar,
  setIsOpenDrawerCreateExperienceSimilar,
}) => {
  const [title, setTitle] = useState<string>('新規エクスペリエンス');
  const [targetURL, setTargetURL] = useState('http://');
  const [device, setDevice] = useState('all');
  const [isMessageNotDuplicated, setIsMessageNotDuplicated] = useState<boolean>(false);
  const [isMessageDuplicated, setIsMessageDuplicated] = useState<Project[] | undefined | null>();
  const [searchString, setSearchString] = useState<string>('');

  //checkbox
  const [isBasicInformation, setIsBasicInformation] = useState<boolean>(false);
  const [isTarget, setIsTarget] = useState<boolean>(false);
  const [isGoal, setIsGoal] = useState<boolean>(false);
  const [isAdvancedSetting, setIsAdvancedSetting] = useState<boolean>(false);

  useQuery<GetProjectsQuery, GetProjectsQueryVariables>(GetProjectsDocument, {
    skip: !searchString,
    variables: {
      organization_id: organization_id ?? '',
      search_string: searchString ? `${searchString}` : '',
    },
    context: { clientName: 'planbcd' },
    onCompleted: (data: GetProjectsQuery): void => {
      if (data?.projects.length) {
        setIsMessageNotDuplicated(false);
        setIsMessageDuplicated(data?.projects);
      } else {
        setIsMessageNotDuplicated(true);
        setIsMessageDuplicated(null);
      }
    },
    onError: (error) => {
      console.error(error);
      globalState.setErrors({
        message: 'error',
        name: 'NonFatalError',
        context: { clientName: 'search_console' },
      });
    },
  });

  const [createProject] = useMutation<CreateProjectMutation, CreateProjectMutationVariables>(CreateProjectDocument, {
    context: { clientName: 'planbcd' },
    onCompleted: () => {
      getProjects.refetch({ variables: { organization_id: organization_id } } as Partial<
        Exact<{ organization_id: string; search_string?: InputMaybe<string> | undefined }>
      >);
    },
  });

  /**
   * フォーム初期化
   */
  const clearFormInput = () => {
    setTitle('新規エクスペリエンス');
    setTargetURL('http://');
    setDevice('all');
    setIsMessageNotDuplicated(false);
    setIsMessageDuplicated(undefined);
    setSearchString('');
  };

  /**
   * エクスペリエンス作成
   */
  const createExperiment = () => {
    const _project = {
      title: title,
      url: targetURL,
      $target_devices: device,
      $focus: false,
      is_mobile_web: false,
      running_condition_operator: 'and',
      running_conditions: [
        {
          type: 'Composite',
          data: {
            operator: 'or',
            conditions: [
              {
                data: {
                  name: 'navigator.device',
                  type: 'string',
                  value: 'windows',
                },
                type: 'Equal',
              },
              {
                data: {
                  name: 'navigator.device',
                  type: 'string',
                  value: 'mac',
                },
                type: 'Equal',
              },
            ],
          },
        },
      ],
    };
    createProject(
      mutationVariables({
        organization_id: organization_id,
        project: _project,
      }),
    );
    setIsOpenDrawerCreateExperienceSimilar(false);
    clearFormInput();
  };

  const isDisableSubmitButtonMemo = useMemo(() => {
    if (!/^https?:\/\/[\w/:%#$&?()~.=+-]+$/.test(targetURL)) {
      return true;
    }
    if (!title) {
      return true;
    }
    return false;
  }, [targetURL, title]);

  return (
    <Fragment>
      {/* 引き継ぐ設定を選んで新規エクスペリエンスを作成 */}
      <Drawer anchor="right" open={isOpenDrawerCreateExperienceSimilar}>
        <DrawerContainer>
          <DrawerContentOverflow>
            <Content>
              <PaperContent>
                <Header>
                  <Title>引き継ぐ設定を選んで新規エクスペリエンスを作成</Title>
                </Header>
                <TitleSubComment>
                  【お知らせ】配信比率の最適化がデフォルトではOFFになりました。詳しくは
                  <Link href="https://support.kaizenplatform.net/hc/ja/articles/900004661026" target="_blank">
                    こちら
                  </Link>
                </TitleSubComment>
              </PaperContent>

              <PaperContent>
                <div>
                  タイトル<SmallRed>必須</SmallRed>
                </div>
                <Input
                  type="text"
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setTitle(e.target.value.slice(0, 50))}
                  value={title}
                />
                <NumberOfCharactar>{title?.length}/50</NumberOfCharactar>
              </PaperContent>

              <Space />

              <PaperContent>
                <div>
                  デザインを改善するページのURL<SmallRed>必須</SmallRed>
                </div>
                <FlexInputArea>
                  <FlexInput
                    type="text"
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setTargetURL(e.target.value)}
                    value={targetURL}
                  />
                  <FlexSecondaryButton
                    disabled={/^https?:\/\/[\w/:%#$&?()~.=+-]+$/.test(targetURL) ? false : true}
                    onClick={() => {
                      setSearchString(
                        `page=1&state%5B%5D=running&state%5B%5D=not_active&url=${encodeURIComponent(targetURL)}`,
                      );
                    }}
                  >
                    URL重複チェック
                  </FlexSecondaryButton>
                </FlexInputArea>

                {/* URL重複チェック結果メッセージ */}
                <Fragment>
                  {isMessageNotDuplicated && <NormalMessage>このURLはまだ利用されていません。</NormalMessage>}

                  {isMessageDuplicated?.length && (
                    <div>
                      <AlertMessage>このURLはすでに以下エクスペリエンスで利用されています。</AlertMessage>
                      {isMessageDuplicated.map((experiment: Project) => (
                        <Link href={'#'} key={`experiment_title_${experiment?.id}`}>
                          <div>{experiment?.title}</div>
                        </Link>
                      ))}
                    </div>
                  )}
                </Fragment>
              </PaperContent>

              <PaperContent>
                <div>対象デバイス</div>
                <TitleSubComment>
                  対象デバイスを変更する場合は、「基本情報」と「ターゲット」の引き継ぎをOFFにしてください
                </TitleSubComment>
              </PaperContent>

              <StyledPaperInner>
                <InnerTitle>複製元から引き継ぎする設定内容</InnerTitle>

                <CheckboxWrapper>
                  <FormControlLabelMui
                    control={
                      <Checkbox
                        disabled={false}
                        checked={isBasicInformation}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          setIsBasicInformation(e.target.checked);
                        }}
                      />
                    }
                    label="基本情報"
                  />
                </CheckboxWrapper>

                <UnorderedList>
                  <ListItemHeader>テスト対象</ListItemHeader>
                  <ListItemBody>ページ修正（UIエディター）</ListItemBody>
                  <ListItemHeader>対象デバイス</ListItemHeader>
                  <ListItemBody> PCのみ（又はスマートフォン専用のレスポンシブデザイン）</ListItemBody>
                </UnorderedList>

                <CheckboxWrapper>
                  <FormControlLabelMui
                    control={
                      <Checkbox
                        disabled={false}
                        checked={isTarget}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          setIsTarget(e.target.checked);
                        }}
                      />
                    }
                    label="ターゲット"
                  />
                </CheckboxWrapper>

                <UnorderedList>
                  <ListItemHeader>実行条件</ListItemHeader>
                  <ListItemBody>未設定</ListItemBody>
                </UnorderedList>

                <CheckboxWrapper>
                  <FormControlLabelMui
                    control={
                      <Checkbox
                        disabled={false}
                        checked={isGoal}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          setIsGoal(e.target.checked);
                        }}
                      />
                    }
                    label="ゴール"
                  />
                </CheckboxWrapper>

                <CheckboxWrapper>
                  <FormControlLabelMui
                    control={
                      <Checkbox
                        disabled={false}
                        checked={isAdvancedSetting}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          setIsAdvancedSetting(e.target.checked);
                        }}
                      />
                    }
                    label="詳細設定"
                  />
                </CheckboxWrapper>

                <UnorderedList>
                  <ListItemHeader>実務結合</ListItemHeader>
                  <ListItemBody>100%</ListItemBody>
                  <ListItemHeader>セッション維持時間の設定</ListItemHeader>
                  <ListItemBody> 12時間00分</ListItemBody>
                </UnorderedList>
              </StyledPaperInner>
            </Content>
          </DrawerContentOverflow>
          <DrawerFooter>
            <SecondaryButton
              onClick={() => {
                setIsOpenDrawerCreateExperienceSimilar(false);
                clearFormInput();
              }}
            >
              キャンセル
            </SecondaryButton>
            <PrimaryButton loading={false} disabled={isDisableSubmitButtonMemo} onClick={createExperiment}>
              エクスペリエンスを作成する
            </PrimaryButton>
          </DrawerFooter>
        </DrawerContainer>
      </Drawer>
    </Fragment>
  );
};

export const DrawerCreateExperience = memo(_DrawerCreateExperience);
export const DrawerCreateExperienceSimilar = memo(_DrawerCreateExperienceSimilar);

const DrawerContent = styled('div')(`
  height: calc(100% - 65px);
  overflow-y: auto;
  padding: 40px 40px;
  position: relative;
  width: 864px;
  fieldset {
    border: 1.5px solid ${COLOR_BORDER};
    outline: none;
  }
  .MuiOutlinedInput-root:hover fieldset {
    border: 1.5px solid ${COLOR_BORDER};
    outline: none;
  }
  .MuiOutlinedInput-root.Mui-focused {
    fieldset {
      border: 1.5px solid ${COLOR_EFFECT};
      outline: none;
    }
  }
  ::-webkit-scrollbar-track {
    background-color: #FFF;
  }
  ::-webkit-scrollbar {
    width: 4px;
    background-color: #EAEAEA;
  }
  ::-webkit-scrollbar-thumb {
    background-color: #EAEAEA;
    border: none;
  }
`);

const DrawerContentOverflow = styled(DrawerContent)(`
  overflow-y: scroll;
  padding-bottom: 112px;
`);

const Header = styled('div')(`
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
`);

const Content = styled('div')(`
  height: calc(100% - 64px);
  width: 100%;
`);

const Title = styled(Typography)(`
  color: ${COLOR_TEXT_MAIN};
  font-weight: 700;
  font-size: 24px;
`);

const InnerTitle = styled(Typography)(`
  color: ${COLOR_TEXT_MAIN};
  font-weight: 700;
  font-size: 18px;
`);

const Space = styled('div')(`
  background-color: transparent;
  border: none;
  height: 24px;
`);

const TitleSubComment = styled(Typography)(`
  color: ${COLOR_TEXT_MAIN};
  font-weight: 400;
  font-size: 14px;
  margin: 12px 0;
`);

const PaperContent = styled('div')(`
  color: ${COLOR_TEXT_MAIN};
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 160%;
  margin: 0 0 24px 0;
`);

const StyledPaperInner = styled('div')(`
  align-items: center;
  background: #F5F6F7;
  box-shadow: 0px 1px 9px ${COLOR_SHADOW};
  border-radius: 8px;
  border: 1px solid ${COLOR_BORDER};
  display: block;
  flex-direction: row;
  flex-wrap: wrap;
  font-weight: 700;
  justify-content: space-between;
  padding: 24px;
  margin: 48px 0 150px;
  width: 100%;
`);

const FormControlLabelMui = styled(FormControlLabel)(`
  .MuiTypography-body1 {
    color: ${COLOR_TEXT_MAIN};
  }
  .Mui-checked {
    color: ${COLOR_TEXT_MAIN};
  }
  span {
    font-weight: 700;
  }
`);

const Input = styled('input')(`
  border: 1px solid ${COLOR_BORDER};
  border-radius: 4px;
  color: ${COLOR_TEXT_MAIN};
  font-size: 14px;
  height: 40px;
  margin: 8px 0 0 0;
  padding: 9px 16px 9px 16px;
  width: 100%;
  &:focus, :focus-within, :focus-visible {
    border: 1.5px solid ${COLOR_EFFECT};
    outline-color: ${COLOR_EFFECT};
  }
`);

const CheckboxWrapper = styled('div')(`
  margin: 12px 0 0;
  width: 100%;
`);

const FlexInput = styled(Input)(`
  margin: 8px 0;
  flex-basis: 75%;
`);

const FlexInputArea = styled('div')(`
  align-items: center;
  display: flex; 
`);

const NumberOfCharactar = styled('span')(`
  font-size: 10px; 
  float: right;
  margin: 6px 0 16px;
`);

const SmallRed = styled('span')(`
  color: ${COLOR_WARNING};
  font-size: 10px;
  padding-left: 4px;
`);

const NormalMessage = styled('div')(`
  margin: 8px 0;
`);

const AlertMessage = styled(NormalMessage)(`
  color: ${COLOR_WARNING};
`);

const Link = styled('a')(`
  text-decoration: none;
  color: ${COLOR_TEXT_LINK};
`);

const ListItemHeader = styled('li')(`
  height: 40px;
  padding: 15px 0;
  flex-basis: 200px;
`);

const ListItemBody = styled('li')(`
  height: 40px;
  padding: 15px 0;
  font-weight: 400;
  width: calc(100% - 200px);
`);

const UnorderedList = styled('ul')(`
  align-items: center;
  flex-wrap: wrap;
  display: flex;
  list-style: none;
  text-decoration: none;
`);

const PrimaryButton = styled(LoadingButton)(`
  background: ${COLOR_TEXT_MAIN};
  border-color: ${COLOR_TEXT_MAIN};
  border-radius: 4px;
  color: ${COLOR_BUTTON_TEXT};
  font-weight: 700;
  font-size: 14px;
  height: 40px;
  padding: 12px 24px;
  text-transform: none;
  :hover {
    background: ${COLOR_EFFECT};
    border-color: ${COLOR_EFFECT};
  }
  :disabled {
    background: ${COLOR_DISABLE_BACKGROUND};
    border: ${COLOR_TEXT_DISABLE};
    color: ${COLOR_TEXT_DISABLE};
  }
`);

const SecondaryButton = styled(Button)(`
  background: ${COLOR_BUTTON_BACKGROUND};
  border-radius: 4px;
  border: 1.5px solid ${COLOR_BORDER};
  color: ${COLOR_TEXT_MAIN};
  height: 40px;
  padding: 13px 16px;
  font-size: 14px;
  font-weight: 700;
  margin-right: 16px;
  :hover {
    background: ${COLOR_BUTTON_BACKGROUND};
    color: ${COLOR_EFFECT};
  }
`);

const FlexSecondaryButton = styled(SecondaryButton)(`
  margin-left: 24px;
  margin-right: 0px; 
  width: calc(25% - 24px);
`);
